import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Wand2, Search, Layers,
  MessageSquare, ClipboardList, Sparkles, Star,
  FileText, Target, MoveRight, UploadCloud, Download
} from "lucide-react";
import CavilowLogo from "../components/CavilowLogo";
import { triggerInstall } from "../components/InstallBanner";
import { api } from "../lib/api";

export default function Landing() {
  useEffect(() => {
    // Fire-and-forget visit ping (idempotent per visitor_id stored locally)
    const KEY = "cavilow_visitor_id";
    const existing = localStorage.getItem(KEY);
    api.post("/visits/track", { path: "/", visitor_id: existing || undefined })
      .then((r) => {
        if (r?.data?.visitor_id && !existing) {
          localStorage.setItem(KEY, r.data.visitor_id);
        }
      })
      .catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-[var(--cavilo-bg)] text-[var(--cavilo-ink)]">
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between relative z-10">
        <Link to="/" className="flex items-center">
          <CavilowLogo size={40} textClass="text-2xl text-white" />
        </Link>
        <div className="flex items-center gap-3">
          <button
            data-testid="landing-get-app"
            onClick={() => triggerInstall()}
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#80FFEA] hover:text-white px-4 py-2 transition-colors"
          >
            <Download size={14} /> Get the app
          </button>
          <Link
            to="/login"
            data-testid="landing-login-link"
            className="text-sm font-semibold text-slate-300 hover:text-white px-4 py-2 transition-colors"
          >
            Sign in
          </Link>
          <Link
            to="/signup"
            data-testid="landing-signup-cta"
            className="btn-neon inline-flex items-center gap-2 text-sm !py-2.5 !px-5"
          >
            Get started <ArrowRight size={16} />
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="absolute top-32 -right-20 w-96 h-96 rounded-full bg-[#9D00FF] opacity-[0.18] blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -left-20 w-80 h-80 rounded-full bg-[#00E5FF] opacity-[0.14] blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-24 lg:pt-16 lg:pb-32 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#80FFEA] mb-5">
              Career <span className="text-[#FF6DAE]">+</span> Vision <span className="text-[#FF6DAE]">+</span> Flow
            </p>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-[5.5rem] tracking-tighter leading-[0.98] text-white">
              Build your <span className="text-neon-purple">CV</span>.<br />
              Find your <span className="text-neon">next role</span>.
            </h1>
            <p className="mt-7 text-lg sm:text-xl text-slate-300 max-w-2xl leading-relaxed">
              Cavilow is your all-in-one career companion, built to help you improve your resume, find matching jobs, create tailored cover letters, and prepare for interviews with confidence.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/signup"
                data-testid="hero-upload-cta"
                className="btn-neon inline-flex items-center gap-2 !py-4 !px-7"
              >
                <UploadCloud size={16} /> Upload My CV
              </Link>
              <Link
                to="/signup"
                data-testid="hero-explore-jobs"
                className="btn-glass inline-flex items-center gap-2 !py-4 !px-7"
              >
                <Search size={16} /> Explore Jobs
              </Link>
              <button
                data-testid="hero-get-app"
                onClick={() => triggerInstall()}
                className="inline-flex items-center gap-2 text-[#80FFEA] hover:text-white px-4 py-4 text-sm font-bold transition-colors"
              >
                <Download size={16} /> Download the app
              </button>
            </div>
            <div className="mt-10 flex items-center gap-4 flex-wrap">
              <div className="flex -space-x-2">
                {["#9D00FF", "#00E5FF", "#80FFEA", "#FFD166"].map((c, i) => (
                  <div key={i} className="w-8 h-8 rounded-full border-2 border-[#0A0A12]" style={{ background: c }} />
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => <Star key={i} size={14} className="text-[#FFD166] fill-[#FFD166]" />)}
                <span className="ml-1 text-sm font-semibold text-slate-300">Trusted by candidates worldwide</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up-delay-2 relative mt-14 lg:mt-0">
            <div className="chip chip-amber inline-flex mb-4 float-y">
              <Sparkles size={12} /> +18 boost
            </div>
            <div className="glass-strong p-7 relative" style={{ boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6), 0 0 40px rgba(157,0,255,0.25)" }}>
              <div className="flex items-center justify-between mb-5">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400">ATS Score</div>
                <div className="chip chip-mint">Top 8%</div>
              </div>
              <div className="flex items-end gap-3 mb-1">
                <div className="font-display font-black text-[6.5rem] text-white leading-[0.85]">87</div>
                <div className="pb-3">
                  <div className="text-sm text-slate-400">/ 100</div>
                  <div className="text-xs text-[#80FFEA] font-semibold">+12 vs draft</div>
                </div>
              </div>
              <div className="mt-5 h-2 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: "87%", background: "linear-gradient(90deg, #9D00FF 0%, #00E5FF 100%)", boxShadow: "0 0 12px rgba(0,229,255,0.6)" }} />
              </div>
              <div className="mt-6 grid grid-cols-2 gap-3">
                {[
                  { label: "Keywords", val: 92 },
                  { label: "Impact", val: 81 },
                  { label: "Format", val: 88 },
                  { label: "Contact", val: 100 },
                ].map((b) => (
                  <div key={b.label} className="p-3 rounded-2xl bg-white/[0.03] border border-white/8">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{b.label}</div>
                    <div className="font-display font-black text-2xl text-white">{b.val}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="absolute -bottom-10 -right-8 lg:-right-10 z-10 rounded-2xl glass p-4 w-56 hidden lg:block float-y" style={{ animationDelay: "1s" }}>
              <div className="flex items-center gap-2">
                <span className="grid place-items-center w-8 h-8 rounded-xl bg-[rgba(128,255,234,0.15)] text-[#80FFEA] border border-[rgba(128,255,234,0.3)]">
                  <ShieldCheck size={16} />
                </span>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Match score</div>
                  <div className="font-display font-black text-lg leading-none text-white">94%</div>
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1">
                {["IFRS", "Audit", "Reporting"].map((t) => (
                  <span key={t} className="text-[10px] px-1.5 py-0.5 rounded-md bg-[rgba(128,255,234,0.15)] text-[#80FFEA] font-bold">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAREER + VISION + FLOW */}
      <section className="py-20 sm:py-28 relative">
        <div className="absolute inset-0 dot-bg opacity-20 pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 relative">
          <div className="text-center mb-14 fade-up">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#80FFEA] mb-4">What Cavilow means</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white">
              Career <span className="text-neon-purple">+</span> Vision <span className="text-neon">+</span> Flow
            </h2>
            <p className="mt-5 text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed">
              Cavilow brings together your career profile, clear job-market guidance, and a smoother application journey — from CV upload to interview preparation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <CvfCard icon={FileText} title="Career" tagline="Your experience, clearly presented."
              body="Turn your skills, work history, and achievements into a stronger, ATS-friendly CV that lands."
              color="#9D00FF" bg="rgba(157,0,255,0.14)" delay={1} />
            <CvfCard icon={Target} title="Vision" tagline="Know where you fit best."
              body="See the right roles, matching jobs, missing skills, and resume improvements — all based on your profile."
              color="#00E5FF" bg="rgba(0,229,255,0.14)" delay={2} />
            <CvfCard icon={MoveRight} title="Flow" tagline="Move smoothly from CV to interview."
              body="One seamless journey: resume → job matching → tailored cover letters → application tracking → interview practice."
              color="#FF6DAE" bg="rgba(255,0,127,0.14)" delay={3} />
          </div>
        </div>
      </section>

      {/* Feature strip */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(90deg, rgba(157,0,255,0.14) 0%, rgba(0,229,255,0.14) 100%)" }}>
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6 relative">
          {[
            { n: "13", l: "Career tools" },
            { n: "0–100", l: "ATS scoring" },
            { n: "3×", l: "Resume versions" },
            { n: "41", l: "Interview Qs / role" },
          ].map((s) => (
            <div key={s.l} className="text-center sm:text-left">
              <div className="font-display font-black text-3xl sm:text-4xl text-neon">{s.n}</div>
              <div className="text-xs uppercase tracking-wider text-slate-300 font-semibold mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <div className="chip chip-amber mb-3"><Sparkles size={12} /> The full kit</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white">
              Everything you need,<br />nothing you don&apos;t.
            </h2>
          </div>
          <p className="text-slate-400 max-w-md">
            One sign-in. One resume. Every output an ATS, recruiter, or hiring manager could want.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { i: ShieldCheck, t: "ATS Review", d: "Score 0-100 with concrete fixes — no fluff.", c: "#9D00FF", bg: "rgba(157,0,255,0.14)" },
            { i: Layers, t: "3 Resume Versions", d: "ATS-friendly, modern, and one-page. In seconds.", c: "#00E5FF", bg: "rgba(0,229,255,0.14)" },
            { i: Wand2, t: "Tailor to JD", d: "Paste a job description. Get a targeted resume + cover letter.", c: "#80FFEA", bg: "rgba(128,255,234,0.14)" },
            { i: Search, t: "Find Jobs", d: "Search LinkedIn, Indeed, Naukri, Bayt, GulfTalent, SEEK, Reed & more.", c: "#FF6DAE", bg: "rgba(255,0,127,0.14)" },
            { i: MessageSquare, t: "Interview Practice", d: "Up to 41 questions per role with sample answers from your story.", c: "#C580FF", bg: "rgba(197,128,255,0.14)" },
            { i: ClipboardList, t: "Application Tracker", d: "Kanban board so an opportunity never falls through.", c: "#FFD166", bg: "rgba(255,209,102,0.14)" },
          ].map((f, i) => (
            <div key={f.t} className={`glass p-7 hover-lift fade-up-delay-${(i % 3) + 1}`}>
              <span className="grid place-items-center w-12 h-12 rounded-2xl mb-5 border border-white/10" style={{ background: f.bg, color: f.c, boxShadow: `0 0 20px ${f.c}44` }}>
                <f.i size={22} />
              </span>
              <div className="font-display font-bold text-xl text-white">{f.t}</div>
              <div className="text-sm text-slate-400 mt-2 leading-relaxed">{f.d}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="rounded-[2rem] p-10 sm:p-14 relative overflow-hidden neon-pulse" style={{ background: "linear-gradient(135deg, #0A0A12 0%, #14142B 60%, rgba(157,0,255,0.35) 100%)", border: "1px solid rgba(255,255,255,0.08)" }}>
          <div className="absolute inset-0 dot-bg opacity-20 pointer-events-none" />
          <div className="absolute top-10 right-10 w-40 h-40 rounded-full bg-[#9D00FF]/40 blur-3xl" />
          <div className="absolute -bottom-10 -left-10 w-56 h-56 rounded-full bg-[#00E5FF]/30 blur-3xl" />
          <div className="relative max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#80FFEA] mb-4">Career + Vision + Flow</p>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-tight text-white">
              Apply with the resume<br />the role <span className="text-neon">actually wants</span>.
            </h2>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed">
              Start in 30 seconds. Upload your resume, score it, tailor it, and ship better applications by lunch.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/signup" data-testid="cta-band-signup" className="btn-neon inline-flex items-center gap-2 !py-3.5 !px-7">
                <UploadCloud size={16} /> Upload My CV
              </Link>
              <Link to="/login" className="text-sm font-semibold text-slate-300 hover:text-white">Sign in instead →</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center text-xs text-slate-500">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 space-y-3">
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-semibold">
            <Link to="/privacy" data-testid="footer-privacy" className="text-slate-400 hover:text-[#80FFEA] transition-colors">Privacy Policy</Link>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <Link to="/terms" data-testid="footer-terms" className="text-slate-400 hover:text-[#80FFEA] transition-colors">Terms of Use</Link>
            <span className="w-1 h-1 rounded-full bg-white/20" />
            <Link to="/contact" data-testid="footer-contact" className="text-slate-400 hover:text-[#80FFEA] transition-colors">Contact &amp; Support</Link>
          </div>
          <p className="text-slate-500">Cavilow is a Reivora product.</p>
          <p>© {new Date().getFullYear()} Reivora. All rights reserved. Cavilow is a Reivora product.</p>
        </div>
      </footer>
    </div>
  );
}

function CvfCard({ icon: Icon, title, tagline, body, color, bg, delay }) {
  return (
    <div className={`glass p-8 hover-lift fade-up-delay-${delay}`}>
      <span className="grid place-items-center w-14 h-14 rounded-2xl mb-6 border border-white/10" style={{ background: bg, color: color, boxShadow: `0 0 24px ${color}55` }}>
        <Icon size={24} />
      </span>
      <h3 className="font-display font-black text-2xl tracking-tight" style={{ color }}>{title}</h3>
      <p className="mt-2 text-base font-bold text-white">{tagline}</p>
      <p className="mt-3 text-sm text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
}
